import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1774500338670-bd4c9cf09166?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwZ2FyZGVuJTIwbGFuZHNjYXBpbmclMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg0NjQ2MDE3fDA&ixlib=rb-4.1.0&q=85";

const lines = ["Ihr Garten,", "in besten", "Händen."];

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.35 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: {
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1] },
  },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen pt-28 sm:pt-32 pb-16 overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-8 items-end">
        {/* Left: text */}
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="hero-overline"
            className="text-xs sm:text-sm font-semibold uppercase tracking-[0.28em] text-yx-secondary mb-6"
          >
            YardTF · Gartenservice
          </motion.p>

          <motion.h1
            variants={lineParent}
            initial="hidden"
            animate="show"
            data-testid="hero-heading"
            className="font-display font-extrabold leading-[0.92] tracking-tighter text-yx-primary text-5xl sm:text-7xl lg:text-8xl"
          >
            {lines.map((line, i) => (
              <span key={i} className="reveal-mask">
                <motion.span variants={lineChild} className="block">
                  {line}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 max-w-md text-base sm:text-lg text-yx-muted leading-relaxed"
          >
            Rasenpflege, Heckenschnitt, Baumpflege, Pflanzenpflege & mehr — gepflegt,
            zuverlässig und mit Liebe zum Detail. Sagen Sie uns, was Ihr Garten
            braucht.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("kontakt")}
              className="group inline-flex items-center gap-2 rounded-full bg-yx-primary px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-yx-secondary hover:shadow-xl hover:shadow-yx-primary/20"
            >
              Kostenlose Anfrage
              <ArrowDownRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5"
              />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("leistungen")}
              className="inline-flex items-center gap-2 rounded-full border border-yx-border px-7 py-3.5 text-sm font-semibold text-yx-primary transition-all duration-300 hover:border-yx-primary"
            >
              Unsere Leistungen
            </button>
          </motion.div>
        </div>

        {/* Right: clipped parallax image */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative aspect-[3/4] w-full overflow-hidden rounded-sm bg-yx-surface"
          >
            <motion.img
              src={HERO_IMG}
              alt="Gepflegte Gartenlandschaft"
              style={{ y: imgY, scale }}
              className="absolute inset-0 h-[120%] w-full object-cover"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-black/5" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-white">
              <span className="text-xs font-medium uppercase tracking-[0.2em] drop-shadow">
                Est. 2025
              </span>
              <span className="font-display text-4xl font-bold drop-shadow">
                Y
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
